/**
 * "How did you hear about us?" — the one list of answers, and the small
 * amount of shared logic around asking it (2026-09-16).
 *
 * Two doors write the same two profile columns:
 *   - the Create-account form (ui/auth-button.js) sends the answer as
 *     signup metadata, and handle_new_user() copies it onto the profile
 *     as the row is created;
 *   - Google sign-ups carry no metadata, so ui/signup-source-prompt.js
 *     asks once after the first sign-in and writes the columns directly.
 *
 * The admin Explorers tab reads the codes back through labelFor().
 *
 * The analytics layer records the OBSERVED first touch (UTM/referrer);
 * this is what the person SAYS. A podcast mention, a friend's text and an
 * AI answer all show up as "direct" in the referrer data — the reason the
 * question is worth asking at all.
 */
import * as gateway from './gateway.js';

/** Codes are stored; labels are what the form shows. Order = form order. */
export const SIGNUP_SOURCES = [
  { code: 'friend', label: 'A friend or family member', detailPrompt: 'Who? (optional)' },
  { code: 'social', label: 'Social media', detailPrompt: 'Which one? (optional)' },
  { code: 'search', label: 'Google or another search engine', detailPrompt: 'What did you search for? (optional)' },
  { code: 'ai', label: 'ChatGPT, Claude or another AI assistant', detailPrompt: 'Which one? (optional)' },
  { code: 'press', label: 'An article, blog or podcast', detailPrompt: 'Which one? (optional)' },
  { code: 'event', label: 'In person — an event, a sticker, a card', detailPrompt: 'Where? (optional)' },
  { code: 'other', label: 'Somewhere else', detailPrompt: 'Where? (optional)' },
];

export const DETAIL_MAX = 200;

/** Ask new accounts only; older accounts never see the prompt. */
export const PROMPT_WINDOW_DAYS = 30;

const KNOWN = new Set(SIGNUP_SOURCES.map((s) => s.code));

/** A code we recognise, or null — never trust a value straight from a form. */
export function normalizeSource(code) {
  const c = (code || '').trim();
  return KNOWN.has(c) ? c : null;
}

/** Trimmed, capped free text, or null when empty. */
export function normalizeDetail(text) {
  const t = (text || '').trim().slice(0, DETAIL_MAX);
  return t || null;
}

/** Plain-words label for a stored code (admin tab); unknown codes show as-is. */
export function labelFor(code) {
  if (!code) return '';
  return SIGNUP_SOURCES.find((s) => s.code === code)?.label || code;
}

/** Per-option placeholder for the free-text follow-up. */
export function detailPromptFor(code) {
  return SIGNUP_SOURCES.find((s) => s.code === code)?.detailPrompt || 'Tell us more (optional)';
}

/**
 * The <select> shared by the signup form and the prompt. Both are plain
 * labeled controls (familiarity rules); the free-text input beside it is
 * hidden until a choice is made, so the empty form asks one question.
 */
export function sourceFieldHtml({ name = 'signup_source', detailName = 'signup_source_detail', current = '' } = {}) {
  return `
    <label class="v2-field">How did you hear about us? <span class="v2-field-optional">(optional)</span>
      <select name="${name}" data-signup-source>
        <option value="">Choose one…</option>
        ${SIGNUP_SOURCES.map((s) => `<option value="${s.code}" ${s.code === current ? 'selected' : ''}>${s.label}</option>`).join('')}
      </select>
    </label>
    <label class="v2-field" data-signup-source-detail hidden>
      <span class="visually-hidden">More about where you heard of us</span>
      <input type="text" name="${detailName}" maxlength="${DETAIL_MAX}" autocomplete="off"
        placeholder="${detailPromptFor(current)}">
    </label>`;
}

/**
 * Wire the show-on-choice behaviour for a form that holds sourceFieldHtml().
 * Idempotent per element; safe on a form without the field.
 */
export function wireSourceField(form) {
  const select = form?.querySelector('[data-signup-source]');
  const detail = form?.querySelector('[data-signup-source-detail]');
  if (!select || !detail || select.dataset.wired) return;
  select.dataset.wired = '1';
  const sync = () => {
    const code = select.value;
    detail.hidden = !code;
    const input = detail.querySelector('input');
    if (input) input.placeholder = detailPromptFor(code);
  };
  select.addEventListener('change', sync);
  sync();
}

/** Read the field back off a form: { signupSource, signupSourceDetail } or nulls. */
export function readSourceField(form) {
  const signupSource = normalizeSource(form?.elements?.signup_source?.value);
  return {
    signupSource,
    signupSourceDetail: signupSource ? normalizeDetail(form?.elements?.signup_source_detail?.value) : null,
  };
}

// ---------------------------------------------------------------------------
// "Have we asked on this device?" — localStorage, so the prompt never nags.
//
// The Create-account form has no user id yet (and none at all when email
// confirmation is on), so it sets a PENDING mark; the prompt converts it to
// a per-user mark the first time it sees that person signed in. A skip on
// the prompt itself marks the user directly. Another device within the
// window asks once more; that is the whole cost of not adding a column.
// ---------------------------------------------------------------------------
const PENDING_KEY = 'yoink_signup_source_asked_pending';
const USER_KEY = (userId) => `yoink_signup_source_asked:${userId}`;

function lsGet(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}
function lsSet(key, value) {
  try { localStorage.setItem(key, value); } catch { /* private mode / quota */ }
}
function lsRemove(key) {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
}

/** The Create-account form was shown with the question on it. */
export function markAskedPending() {
  lsSet(PENDING_KEY, '1');
}

/** This person has been asked (or answered) on this device. */
export function markAsked(userId) {
  if (!userId) return;
  lsSet(USER_KEY(userId), '1');
  lsRemove(PENDING_KEY);
}

/**
 * Was this person already asked here? Also settles a pending mark left by
 * the signup form onto the user it turned out to be.
 */
export function wasAsked(userId) {
  if (!userId) return true;
  if (lsGet(USER_KEY(userId))) return true;
  if (lsGet(PENDING_KEY)) {
    markAsked(userId);
    return true;
  }
  return false;
}

/** Account young enough to be asked at all. */
export function withinPromptWindow(createdAt, now = Date.now()) {
  const t = createdAt ? new Date(createdAt).getTime() : NaN;
  if (Number.isNaN(t)) return false;
  return now - t <= PROMPT_WINDOW_DAYS * 86400000;
}

/**
 * The prompt's one read: does this profile still owe an answer? Deliberately
 * NOT folded into the boot-time profile select in core/auth.js — until the
 * migration is applied that column does not exist, and PostgREST 400s the
 * WHOLE select for one unknown column, which would blank the header name
 * and every signed-in surface. Here a 400 means "not live yet": { ask: false }.
 */
export async function fetchPromptState(userId) {
  const { data, error } = await gateway.rawQuery(gateway.TABLES.PROFILES, {
    select: 'id,created_at,signup_source',
    filters: { id: `eq.${userId}` },
    limit: 1,
  });
  if (error || !data?.[0]) return { ask: false, reason: error ? 'unavailable' : 'no-profile' };
  const row = data[0];
  if (row.signup_source) return { ask: false, reason: 'answered' };
  if (!withinPromptWindow(row.created_at)) return { ask: false, reason: 'not-new' };
  return { ask: true };
}

/** Write the answer onto the caller's own profile (RLS: auth.uid() = id). */
export async function saveSignupSource(userId, code, detail) {
  const signup_source = normalizeSource(code);
  if (!signup_source) return { error: new Error('Pick an option first.') };
  const { error } = await gateway.rawUpdate(gateway.TABLES.PROFILES, {
    filters: { id: `eq.${userId}` },
    body: { signup_source, signup_source_detail: normalizeDetail(detail) },
  });
  return { error: error || null };
}
